import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { CreditCard, Lock, Server, CheckCircle, ArrowRight, Zap, Shield, Wallet, Coins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PopularSubs from '../components/Hydrixpay/PopularSubs';
import Perks from '../components/Hydrixpay/Perks';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('assets/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('assets/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('assets/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('assets/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    overflow-x: hidden;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const GradientOrb = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
  pointer-events: none;
`;

const Container = styled.div`
  width: 80%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeroSection = styled.section`
  min-height: 85vh;
  display: flex;
  align-items: center;
  padding: 6rem 0;
  position: relative;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-family: 'Very Sans Headline', sans-serif;
  font-size: 4rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-family: 'Very Sans', sans-serif;
  font-size: 1.3rem;
  color: #94A3B8;
  margin-bottom: 3rem;
  line-height: 1.7;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-family: 'Very Sans', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563EB;
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #94A3B8;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;
  border-radius: 8px;
  font-family: 'Very Sans', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.3);
    color: #ffffff;
  }
`;

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-3px);
  }
`;

const StatNumber = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-family: 'Very Sans', sans-serif;
  color: #94A3B8;
  font-size: 0.9rem;
`;

const FeaturesSection = styled.section`
  padding: 6rem 0;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-family: 'Very Sans Headline', sans-serif;
  font-size: 3rem;
  font-weight: 600;
  text-align: center;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-family: 'Very Sans', sans-serif;
  text-align: center;
  font-size: 1.2rem;
  color: #94A3B8;
  max-width: 700px;
  margin: 0 auto 3rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.accentColor || '#3B82F6'};
    opacity: 0.8;
  }

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 12px 30px rgba(59, 130, 246, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: ${props => props.bg || 'rgba(59, 130, 246, 0.1)'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const FeatureDescription = styled.p`
  font-family: 'Very Sans', sans-serif;
  color: #94A3B8;
  line-height: 1.7;
  font-size: 1rem;
`;

const HowItWorksSection = styled.section`
  padding: 8rem 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(139, 92, 246, 0.02) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 20%;
    left: 5%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(80px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 20%;
    right: 5%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(80px);
  }
`;

const HowItWorksContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 90px;
    left: 16.6%;
    right: 16.6%;
    height: 3px;
    background: linear-gradient(90deg, 
      rgba(59, 130, 246, 0.5) 0%, 
      rgba(139, 92, 246, 0.5) 50%, 
      rgba(236, 72, 153, 0.5) 100%
    );
    z-index: 0;
    
    @media (max-width: 968px) {
      display: none;
    }
  }
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2.5rem;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || 'linear-gradient(90deg, #3B82F6, #8b5cf6)'};
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 300px;
    height: 300px;
    background: ${props => props.glowColor || 'rgba(59, 130, 246, 0.05)'};
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.04);
    border-color: ${props => props.borderColor || 'rgba(59, 130, 246, 0.3)'};
    box-shadow: ${props => props.shadow || '0 20px 50px rgba(59, 130, 246, 0.25)'};
    
    &::after {
      opacity: 1;
    }
  }
`;

const StepNumberWrapper = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

const StepNumber = styled.div`
  width: 90px;
  height: 90px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2.5rem;
  color: white;
  box-shadow: ${props => props.shadow || '0 8px 30px rgba(59, 130, 246, 0.4)'};
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    background: ${props => props.gradient || 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'};
    opacity: 0.3;
    filter: blur(10px);
  }
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.6rem;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  font-family: 'Very Sans', sans-serif;
  color: #94A3B8;
  line-height: 1.7;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
`;

const StepFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
`;

const StepFeature = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Very Sans', sans-serif;
  color: #CBD5E1;
  font-size: 0.95rem;
  
  &::before {
    content: '✓';
    width: 24px;
    height: 24px;
    background: ${props => props.checkBg || 'rgba(59, 130, 246, 0.15)'};
    color: ${props => props.checkColor || '#3B82F6'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-weight: bold;
  }
`;

const CTAButtonCentered = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 4rem;
`;

const AboutSection = styled.section`
  padding: 6rem 0;
  position: relative;
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutText = styled.div``;

const AboutTitle = styled.h2`
  font-family: 'Very Sans Headline', sans-serif;
  font-size: 3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #ffffff;
`;

const AboutDescription = styled.p`
  font-family: 'Very Sans', sans-serif;
  font-size: 1.1rem;
  color: #94A3B8;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${props => props.accentColor || '#22c55e'};
    opacity: 0.8;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
  }
`;

const BenefitIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: ${props => props.bg || 'rgba(34, 197, 94, 0.1)'};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h4`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.1rem;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const BenefitDescription = styled.p`
  font-family: 'Very Sans', sans-serif;
  color: #94A3B8;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const AboutStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
`;

const AboutStatCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-3px);
  }
`;

const AboutStatNumber = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const AboutStatLabel = styled.div`
  font-family: 'Very Sans', sans-serif;
  color: #94A3B8;
  font-size: 0.9rem;
`;

const CTASection = styled.section`
  padding: 6rem 0;
  position: relative;
`;

const CTAContainer = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  padding: 4rem;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-family: 'Very Sans Headline', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-family: 'Very Sans', sans-serif;
  font-size: 1.2rem;
  color: #94A3B8;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

export default function Hydrixpay() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <CreditCard size={28} />,
      title: 'Virtual Cards',
      description: 'Your own virtual card with real-time spending controls, instant notifications, and advanced security features.',
      accentColor: '#3B82F6', // Blue
      iconBg: 'rgba(59, 130, 246, 0.15)',
      iconColor: '#3B82F6'
    },
    {
      icon: <Zap size={28} />,
      title: 'Automation',
      description: 'Our servers automate all transactions - sign once, pay forever while staying fully in control.',
      accentColor: '#f59e0b', // Amber
      iconBg: 'rgba(245, 158, 11, 0.15)',
      iconColor: '#f59e0b'
    },
    {
      icon: <Shield size={28} />,
      title: 'Bank-Level Security',
      description: 'Your money and data are protected with enterprise-grade encryption, biometric authentication, and fraud monitoring.',
      accentColor: '#22c55e', // Green
      iconBg: 'rgba(34, 197, 94, 0.15)',
      iconColor: '#22c55e'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Sign Up',
      description: 'Complete KYC verification and get your free virtual card instantly.',
      features: [
        'Quick 5-minute setup',
        'Instant card generation',
        'No monthly fees'
      ],
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      glowColor: 'rgba(59, 130, 246, 0.08)',
      borderColor: 'rgba(59, 130, 246, 0.4)',
      shadow: '0 20px 50px rgba(59, 130, 246, 0.3)',
      checkBg: 'rgba(59, 130, 246, 0.15)',
      checkColor: '#3B82F6'
    },
    {
      number: 2,
      title: 'Set Up Payments',
      description: 'Connect your wallet and configure your subscription payments.',
      features: [
        'Connect any wallet',
        'Set spending limits',
        'Choose payment schedule'
      ],
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      glowColor: 'rgba(139, 92, 246, 0.08)',
      borderColor: 'rgba(139, 92, 246, 0.4)',
      shadow: '0 20px 50px rgba(139, 92, 246, 0.3)',
      checkBg: 'rgba(139, 92, 246, 0.15)',
      checkColor: '#8b5cf6'
    },
    {
      number: 3,
      title: 'Sign & Relax',
      description: 'Sign one transaction and let automation handle the rest.',
      features: [
        'One-time signature',
        'Auto-pay subscriptions',
        'Earn rewards instantly'
      ],
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      glowColor: 'rgba(236, 72, 153, 0.08)',
      borderColor: 'rgba(236, 72, 153, 0.4)',
      shadow: '0 20px 50px rgba(236, 72, 153, 0.3)',
      checkBg: 'rgba(236, 72, 153, 0.15)',
      checkColor: '#ec4899'
    }
  ];

  const benefits = [
    {
      title: 'Non-Custodial',
      description: 'You maintain full control of your funds. We never have access to your private keys.',
      icon: <Wallet size={24} />,
      accentColor: '#3B82F6',
      iconBg: 'rgba(59, 130, 246, 0.15)',
      iconColor: '#3B82F6'
    },
    {
      title: 'Automated Payments',
      description: 'Set it and forget it. Sign once and let our servers handle recurring payments.',
      icon: <Zap size={24} />,
      accentColor: '#f59e0b',
      iconBg: 'rgba(245, 158, 11, 0.15)',
      iconColor: '#f59e0b'
    },
    {
      title: 'Earn Rewards',
      description: 'Get cashback on subscriptions and everyday purchases automatically.',
      icon: <Coins size={24} />,
      accentColor: '#22c55e',
      iconBg: 'rgba(34, 197, 94, 0.15)',
      iconColor: '#22c55e'
    },
    {
      title: 'Bank-Level Security',
      description: 'Enterprise-grade encryption and fraud monitoring keep your money safe.',
      icon: <Lock size={24} />,
      accentColor: '#8b5cf6',
      iconBg: 'rgba(139, 92, 246, 0.15)',
      iconColor: '#8b5cf6'
    }
  ];

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <GradientOrb />

        <HeroSection>
          <Container>
            <HeroContent>
              <HeroTitle>
                Pay & Earn with <Highlight>Crypto</Highlight>
              </HeroTitle>
              <HeroSubtitle>
                Set up recurring payments from your non-custodial wallet and earn rewards
                while you spend. Simple, secure, and fully automated.
              </HeroSubtitle>
              <CTAButtons>
                <PrimaryButton onClick={() => navigate('/virtualcard')}>
                  Try Sandbox
                  <ArrowRight size={20} />
                </PrimaryButton>
                <SecondaryButton>Learn More</SecondaryButton>
              </CTAButtons>
              <HeroStats>
                <StatItem>
                  <StatNumber>10K+</StatNumber>
                  <StatLabel>Active Users</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>99.9%</StatNumber>
                  <StatLabel>Uptime</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>24/7</StatNumber>
                  <StatLabel>Support</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>$0</StatNumber>
                  <StatLabel>Monthly Fees</StatLabel>
                </StatItem>
              </HeroStats>
            </HeroContent>
          </Container>
        </HeroSection>

        <PopularSubs />

        <FeaturesSection>
          <Container>
            <SectionTitle>Built for You</SectionTitle>
            <SectionSubtitle>
              Simple, powerful tools that make using your crypto easier.
            </SectionSubtitle>

            <FeaturesGrid>
              {features.map((feature, index) => (
                <FeatureCard key={index} accentColor={feature.accentColor}>
                  <IconWrapper bg={feature.iconBg}>
                    {React.cloneElement(feature.icon, { color: feature.iconColor })}
                  </IconWrapper>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </Container>
        </FeaturesSection>

        <HowItWorksSection>
          <Container>
            <HowItWorksContent>
              <SectionTitle>How It Works</SectionTitle>
              <SectionSubtitle>
                Get started in three simple steps and start earning rewards today.
              </SectionSubtitle>

              <StepsContainer>
                {steps.map((step, index) => (
                  <StepCard 
                    key={index}
                    gradient={step.gradient}
                    glowColor={step.glowColor}
                    borderColor={step.borderColor}
                    shadow={step.shadow}
                  >
                    <StepNumberWrapper>
                      <StepNumber 
                        gradient={step.gradient}
                        shadow={step.shadow}
                      >
                        {step.number}
                      </StepNumber>
                    </StepNumberWrapper>
                    
                    <StepContent>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                      
                      <StepFeatures>
                        {step.features.map((feature, idx) => (
                          <StepFeature 
                            key={idx}
                            checkBg={step.checkBg}
                            checkColor={step.checkColor}
                          >
                            {feature}
                          </StepFeature>
                        ))}
                      </StepFeatures>
                    </StepContent>
                  </StepCard>
                ))}
              </StepsContainer>

              <CTAButtonCentered>
                <PrimaryButton onClick={() => navigate('/virtualcard')}>
                  Try the Sandbox
                  <ArrowRight size={20} />
                </PrimaryButton>
              </CTAButtonCentered>
            </HowItWorksContent>
          </Container>
        </HowItWorksSection>

        <AboutSection>
          <Container>
            <AboutContent>
              <AboutText>
                <AboutTitle>
                  Simple, <Highlight>Non-Custodial</Highlight> Banking
                </AboutTitle>
                <AboutDescription>
                  Trillions sit in crypto wallets unused. We aim to empower regular crypto
                  holders to spend their gains on everyday expenses, just like a regular bank.
                </AboutDescription>
                <AboutDescription>
                  Built with security and user experience as our top priorities, we're here
                  to help you achieve your financial goals without the complexity.
                </AboutDescription>
              </AboutText>

              <BenefitsList>
                {benefits.map((benefit, index) => (
                  <BenefitItem key={index} accentColor={benefit.accentColor}>
                    <BenefitIconWrapper bg={benefit.iconBg}>
                      {React.cloneElement(benefit.icon, { color: benefit.iconColor })}
                    </BenefitIconWrapper>
                    <BenefitText>
                      <BenefitTitle>{benefit.title}</BenefitTitle>
                      <BenefitDescription>{benefit.description}</BenefitDescription>
                    </BenefitText>
                  </BenefitItem>
                ))}
              </BenefitsList>
            </AboutContent>

            
          </Container>
        </AboutSection>

        <Perks />

       
      </PageContainer>
    </>
  );
}